import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { queryClient, invalidateLeadTimeline } from "@/lib/queryClient";
import {
  doc_endpoint,
  doc_upload,
  doc_presign,
  doc_upload_complete,
} from "@/config/endpoints";
import { attachmentFoldersQueryKey } from "@/hooks/api/useAttachmentFolders";
import {
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_SIZE_LABEL,
  formatFileSize,
} from "@/utils/uploadSizeLimit";

// Client-side backstop for the server's 5MB document cap — rejects before the
// presign round-trip so every upload call site gets a clear message instead of
// a server 400. The attachment dropzone (UplaodSection) additionally offers to
// reduce oversized images/PDFs BEFORE calling this mutation.
function assertWithinUploadLimit(file: File) {
  if (file.size <= MAX_UPLOAD_BYTES) return;
  throw Object.assign(
    new Error(`File too large — the limit is ${MAX_UPLOAD_SIZE_LABEL}.`),
    {
      clientMessage: `"${file.name}" is ${formatFileSize(file.size)} — files above ${MAX_UPLOAD_SIZE_LABEL} aren't supported.`,
    }
  );
}

// Doc writes log activity. Lead attachment → that lead's timeline; child →
// prefix (lead id unknown; only the open drawer's timeline is active);
// anything else (course, university) → no timeline.
function invalidateDocTimeline(attachedTo?: string, attachedScope?: unknown) {
  const to = String(attachedTo ?? "").toLowerCase();
  if (to === "lead" || to === "leads") {
    invalidateLeadTimeline(attachedScope as number | string | null);
  } else if (to === "child") {
    // The cached deal row carries its parent lead id — enough to stamp the
    // lead card's "last updated" corner. Unknown → prefix bust as before.
    const cached = queryClient
      .getQueriesData<{ leadId?: unknown }>({
        predicate: (q) =>
          q.queryKey[0] === "get-child" &&
          Number(q.queryKey[1]) === Number(attachedScope),
      })
      .find(([, d]) => d?.leadId != null)?.[1];
    invalidateLeadTimeline((cached?.leadId as number | undefined) ?? null);
  }
}

// Define the payload to match your Backend requirements
interface UploadPayload {
  file: File;
  attachedTo: string;
  attachedScope: string;
  category: string;
  name?: string;
  /** Upload into this folder (omit or null = root) */
  folderId?: number | null;
  /** Defaults to WORKFILE on the server if omitted */
  attachmentLabel?: "WORKFILE" | "PAYMENT_RECEIPT";
  /**
   * Deal grain. The row is stored on the parent lead either way (the server
   * collapses every attachment onto the lead); this only marks WHICH deal the
   * file belongs to. `null`/omitted = a lead document, which is what every
   * caller that never asks the question keeps getting.
   */
  childId?: number | null;
}

/**
 * Legacy proxy upload — the file streams through the API to Spaces. Kept as
 * the FALLBACK for when the direct PUT to Spaces fails (e.g. the bucket's
 * CORS isn't configured yet, or a transient Spaces network error).
 */
async function uploadViaProxy(data: UploadPayload) {
  const formData = new FormData();
  formData.append("attachedTo", data.attachedTo);
  formData.append("attachedScope", data.attachedScope);
  formData.append("category", data.category);
  if (data.childId != null) formData.append("childId", String(data.childId));
  if (data.name) formData.append("name", data.name);
  if (data.folderId != null && data.folderId !== undefined) {
    formData.append("folderId", String(data.folderId));
  }
  if (data.attachmentLabel) {
    formData.append("attachmentLabel", data.attachmentLabel);
  }
  formData.append("file", data.file);
  const response = await axiosInstance.post(doc_upload, formData);
  return response.data;
}

export function useDocumentMutaion() {
  const uploadFile = useMutation({
    mutationFn: async (data: UploadPayload) => {
      assertWithinUploadLimit(data.file);
      // Direct-to-Spaces flow: the API only signs the URL and records the
      // row — the file bytes go browser → Spaces, never through the droplet.
      // 1. Ask the API for a signed PUT URL.
      const presignRes = await axiosInstance.post(doc_presign, {
        fileName: data.file.name,
        fileType: data.file.type || null,
        fileSize: data.file.size,
        category: data.category,
        attachedTo: data.attachedTo,
        attachedScope: data.attachedScope,
      });
      const { uploadUrl, s3Key, contentType } = presignRes.data?.data ?? {};

      // 2. PUT the file straight to the bucket. Plain axios — no auth header,
      // and the headers must match what the server signed: ONLY Content-Type.
      // Do NOT add x-amz-* headers (e.g. x-amz-acl) — they aren't in the
      // signature, so S3 rejects the PUT with 403 SignatureDoesNotMatch;
      // public readability comes from the bucket policy, ACLs are disabled.
      try {
        await axios.put(uploadUrl, data.file, {
          headers: {
            "Content-Type": contentType,
          },
        });
      } catch {
        // CORS not set up / Spaces hiccup — fall back to the old proxy path
        // so the user's upload still lands.
        return uploadViaProxy(data);
      }

      // 3. Record the Document row (server re-verifies the object via HEAD).
      const response = await axiosInstance.post(doc_upload_complete, {
        s3Key,
        fileName: data.file.name,
        category: data.category,
        attachedTo: data.attachedTo,
        attachedScope: data.attachedScope,
        name: data.name,
        folderId: data.folderId,
        attachmentLabel: data.attachmentLabel,
        childId: data.childId ?? null,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success("Document uploaded successfully");
      // Documents on the lead view come from React Query (get-leads) + RHF sync,
      // not from the upload JSON — never use response.attachedTo here (undefined).
      queryClient.invalidateQueries({ queryKey: ["get-leads"] });
      if (String(variables.attachedTo).toLowerCase() === "child") {
        queryClient.invalidateQueries({ queryKey: ["get-child"] });
      }
      queryClient.invalidateQueries({ queryKey: ["childs"] });
      queryClient.invalidateQueries({
        queryKey: attachmentFoldersQueryKey(
          variables.attachedTo,
          variables.attachedScope
        ),
      });
      invalidateDocTimeline(variables.attachedTo, variables.attachedScope);
    },
    onError: (error: any) => {
      console.error("Upload error:", error);
      const msg =
        error.response?.data?.message ||
        error.clientMessage ||
        "Failed to upload document";
      toast.error(msg);
    },
  });

  const updateFile = useMutation({
    mutationFn: async (data: {
      id: string;
      file?: File;
      name?: string;
      attachedTo?: string;
      attachedScope?: string | number;
      folderId?: number | null;
      attachmentLabel?: "WORKFILE" | "PAYMENT_RECEIPT";
      /** Re-file between the lead (null) and one of its deals. */
      childId?: number | null;
    }) => {
      if (data.file) assertWithinUploadLimit(data.file);
      const formData = new FormData();
      if (data.name) formData.append("name", data.name);
      if (data.file) formData.append("file", data.file);
      if (data.folderId !== undefined) {
        formData.append(
          "folderId",
          data.folderId === null ? "null" : String(data.folderId)
        );
      }
      if (data.attachmentLabel) {
        formData.append("attachmentLabel", data.attachmentLabel);
      }
      // "null" (the string) is how the multipart edit path says "back to the
      // lead" — same convention folderId already uses.
      if (data.childId !== undefined) {
        formData.append(
          "childId",
          data.childId === null ? "null" : String(data.childId)
        );
      }

      const response = await axiosInstance.patch(
        `${doc_endpoint}/${data.id}`,
        formData
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success("Document updated successfully");
      queryClient.invalidateQueries({ queryKey: ["get-leads"] });
      if (
        variables.attachedTo &&
        String(variables.attachedTo).toLowerCase() === "child"
      ) {
        queryClient.invalidateQueries({ queryKey: ["get-child"] });
      }
      if (variables.attachedTo != null && variables.attachedScope != null) {
        queryClient.invalidateQueries({
          queryKey: attachmentFoldersQueryKey(
            variables.attachedTo,
            variables.attachedScope
          ),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["childs"] });
      invalidateDocTimeline(variables.attachedTo, variables.attachedScope);
    },
    onError: (error: any) => {
      console.error("Update error:", error);
      const msg =
        error.response?.data?.message ||
        error.clientMessage ||
        "Failed to update document";
      toast.error(msg);
    },
  });

  const deleteFile = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await axiosInstance.delete(`${doc_endpoint}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Document deleted successfully");
      // Only the open drawer's timeline is active, so the prefix is cheap.
      invalidateLeadTimeline();
      // Since we don't know the exact attachedTo from just the ID in the mutation response
      // sometimes, we rely on the component calling invalidateQueries or we invalidate all
      // related queries. The caller can pass onSuccess callback to invalidate specific query.
    },
    onError: (error: any) => {
      console.error("Delete error:", error);
      const msg = error.response?.data?.message || "Failed to delete document";
      toast.error(msg);
    },
  });

  return { uploadFile, updateFile, deleteFile };
}
